import { useState, useMemo } from 'react'
import Modal from './Modal'
import DateRangeCalendar from './DateRangeCalendar'
import { IconChevronDown, IconCalendar } from './Icons'

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const cleanStr = typeof dateStr === 'string' ? dateStr.split('T')[0] : ''
  const parts = cleanStr.split('-')
  if (parts.length === 3) {
    const year = parts[0]
    const monthIdx = parseInt(parts[1], 10) - 1
    const day = parts[2]
    const monthNamesShort = ['янв.', 'февр.', 'мар.', 'апр.', 'мая', 'июня', 'июля', 'авг.', 'сент.', 'окт.', 'нояб.', 'дек.']
    const monthName = monthNamesShort[monthIdx] || parts[1]
    return `${day} ${monthName} ${year}`
  }
  return new Date(dateStr).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }).replace(' г.', '')
}

const isoDate = (d) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Стандартные быстрые пресеты */
function getDefaultPresets() {
  const now = new Date()
  const y = now.getFullYear(), m = now.getMonth()
  return [
    { label: 'Сегодня', getValue: () => { const t = isoDate(now); return [t, t] } },
    { label: 'Неделя', getValue: () => { const d = new Date(now); d.setDate(d.getDate() - 6); return [isoDate(d), isoDate(now)] } },
    { label: 'Этот месяц', getValue: () => [isoDate(new Date(y, m, 1)), isoDate(new Date(y, m + 1, 0))] },
    { label: 'Пред. месяц', getValue: () => [isoDate(new Date(y, m - 1, 1)), isoDate(new Date(y, m, 0))] },
    { label: 'Квартал', getValue: () => { const qm = Math.floor(m / 3) * 3; return [isoDate(new Date(y, qm, 1)), isoDate(new Date(y, qm + 3, 0))] } },
    { label: 'Год', getValue: () => [isoDate(new Date(y, 0, 1)), isoDate(new Date(y, 11, 31))] },
  ]
}

export default function DateRangeFilter({
  startDate,
  endDate,
  onApply,
  onReset,
  quickPresets = [],
  isSingleDate = false,
  showQuickButtons = false,
  shiftMarkers = null,
  onBadgeClick = null,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [localStart, setLocalStart] = useState(startDate || '')
  const [localEnd, setLocalEnd] = useState(endDate || '')
  const [selectionStep, setSelectionStep] = useState('start')

  const defaultPresets = useMemo(() => getDefaultPresets(), [])
  const allPresets = quickPresets.length > 0 ? quickPresets : defaultPresets

  const handleOpen = () => {
    setLocalStart(startDate || '')
    setLocalEnd(endDate || '')
    setSelectionStep('start')
    setIsOpen(true)
  }

  const handleDateSelect = (dateStr) => {
    if (isSingleDate) {
      setLocalStart(dateStr)
      setLocalEnd(dateStr)
      return
    }
    if (selectionStep === 'start') {
      setLocalStart(dateStr)
      setLocalEnd('')
      setSelectionStep('end')
    } else {
      if (dateStr < localStart) {
        setLocalStart(dateStr)
        setLocalEnd('')
        setSelectionStep('end')
      } else {
        setLocalEnd(dateStr)
        setSelectionStep('start')
      }
    }
  }

  const handleApply = () => {
    onApply(localStart, localEnd)
    setIsOpen(false)
  }

  const handleReset = () => {
    setLocalStart('')
    setLocalEnd('')
    onReset()
    setIsOpen(false)
  }

  // Определяем какой пресет активен
  const activePresetLabel = useMemo(() => {
    if (!startDate || !endDate) return null
    for (const p of allPresets) {
      const [s, e] = p.getValue()
      if (s === startDate && e === endDate) return p.label
    }
    return null
  }, [startDate, endDate, allPresets])

  const getDisplayLabel = () => {
    if (!startDate && !endDate) return 'За всё время'
    if (startDate === endDate) return formatDate(startDate)
    if (startDate && !endDate) return `С ${formatDate(startDate)}`
    if (!startDate && endDate) return `По ${formatDate(endDate)}`
    return `${formatDate(startDate)} — ${formatDate(endDate)}`
  }

  return (
    <>
      <button 
        type="button"
        className="btn btn-ghost" 
        onClick={handleOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'var(--surface)',
          border: '1px solid rgba(226, 232, 240, 0.6)',
          borderRadius: '30px',
          padding: '10px 18px',
          color: 'var(--text-primary)',
          fontWeight: 600,
          width: '100%',
          justifyContent: 'space-between',
          fontSize: 'var(--font-size-sm)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconCalendar size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
          <span>{getDisplayLabel()}</span>
        </span>
        <IconChevronDown size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
      </button>

      {/* Быстрые кнопки периодов — аккуратная адаптивная плашка */}
      {showQuickButtons && !isSingleDate && (
        <div 
          style={{ 
            display: 'flex', 
            gap: '6px', 
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: '8px', 
            paddingBottom: '2px',
          }} 
          onTouchMove={e => e.stopPropagation()}
        >
          {allPresets.map(preset => {
            const isActive = preset.label === activePresetLabel
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => {
                  const [s, e] = preset.getValue()
                  onApply(s, e)
                }}
                style={{
                  padding: '5px 12px',
                  borderRadius: '20px',
                  border: isActive ? '1.5px solid var(--wb-primary)' : '1px solid var(--surface-border)',
                  background: isActive ? 'var(--wb-primary)' : 'var(--surface)',
                  color: isActive ? 'white' : 'var(--text-secondary)',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  boxShadow: isActive ? '0 2px 8px rgba(59,130,246,0.3)' : '0 1px 3px rgba(0,0,0,0.02)'
                }}
              >
                {preset.label}
              </button>
            )
          })}
        </div>
      )}

      <Modal 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)} 
        title="Выберите период" 
        isBottomSheet={true}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '10px 0 20px 0' }}>
          
          {!isSingleDate && allPresets.length > 0 && (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px', textTransform: 'uppercase' }}>Быстрый выбор</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {allPresets.map(preset => (
                  <button
                    key={preset.label}
                    className="btn btn-ghost btn-sm"
                    style={{ 
                      background: 'var(--bg-secondary)', 
                      border: '1px solid rgba(226, 232, 240, 0.5)',
                      borderRadius: '30px',
                      fontWeight: 600,
                      color: 'var(--text-primary)'
                    }}
                    onClick={() => {
                      const [s, e] = preset.getValue()
                      setLocalStart(s)
                      setLocalEnd(e)
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
              <div 
                style={{ flex: 1, padding: '8px', textAlign: 'center', borderRadius: '8px', border: (!isSingleDate && selectionStep === 'start') ? '2px solid var(--wb-primary)' : '1px solid var(--surface-border)', cursor: 'pointer', background: (!isSingleDate && selectionStep === 'start') ? 'var(--wb-primary-ultra-light)' : 'transparent', transition: 'all 0.2s' }}
                onClick={() => !isSingleDate && setSelectionStep('start')}
              >
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px' }}>{isSingleDate ? 'Дата' : 'Начало'}</div>
                <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{localStart ? formatDate(localStart) : 'Выберите'}</div>
              </div>
              {!isSingleDate && (
                <>
                  <div style={{ width: '12px' }}></div>
                  <div 
                    style={{ flex: 1, padding: '8px', textAlign: 'center', borderRadius: '8px', border: selectionStep === 'end' ? '2px solid var(--wb-primary)' : '1px solid var(--surface-border)', cursor: 'pointer', background: selectionStep === 'end' ? 'var(--wb-primary-ultra-light)' : 'transparent', transition: 'all 0.2s' }}
                    onClick={() => setSelectionStep('end')}
                  >
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '4px' }}>Конец</div>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{localEnd ? formatDate(localEnd) : 'Выберите'}</div>
                  </div>
                </>
              )}
            </div>

            <DateRangeCalendar 
              startDate={localStart} 
              endDate={localEnd} 
              onDateSelect={handleDateSelect}
              shiftMarkers={shiftMarkers}
              onBadgeClick={onBadgeClick ? (date, empId) => { setIsOpen(false); onBadgeClick(date, empId) } : null}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '12px' }}>
            <button type="button" className="btn btn-primary" style={{ flex: '2', padding: '14px' }} onClick={handleApply}>
              Применить
            </button>
            <button type="button" className="btn btn-ghost" style={{ flex: '1', padding: '14px', background: 'var(--bg-secondary)' }} onClick={handleReset}>
              Сбросить
            </button>
          </div>
        </div>
      </Modal>
    </>
  )
}
